const calculateForecastBalance = (onlyAssets, openingVal, growth, risk) => {
  if (onlyAssets && risk <= 0) {
    return 0
  }
  return openingVal * (1 + (growth / 10000))
}

export const calculateForecastValues = (onlyAssets, openingBalances, growthsAndRisks) => {
  const { homeLoansGrowth, businessLoansGrowth, creditCardsGrowth, institutionalBankingGrowth, offshoreBankGrowth, wealthGrowth, investmentsGrowth, fixedAssetsGrowth } = growthsAndRisks

  const homeLoansForecast = calculateForecastBalance(onlyAssets, openingBalances.homeLoans, homeLoansGrowth, growthsAndRisks.homeLoansRisk)
  const businessLoansForecast = calculateForecastBalance(onlyAssets, openingBalances.businessLoans, businessLoansGrowth, growthsAndRisks.businessLoansRisk)
  const creditCardsForecast = calculateForecastBalance(onlyAssets, openingBalances.creditCards, creditCardsGrowth, growthsAndRisks.creditCardsRisk)
  const institutionalBankingForecast = calculateForecastBalance(onlyAssets, openingBalances.institutionalBanking, institutionalBankingGrowth, growthsAndRisks.institutionalBankingRisk)
  const offshoreBankForecast = calculateForecastBalance(onlyAssets, openingBalances.offshoreBank, offshoreBankGrowth, growthsAndRisks.offshoreBankRisk)
  const wealthForecast = calculateForecastBalance(onlyAssets, openingBalances.wealthAssets, wealthGrowth, growthsAndRisks.wealthRisk)
  const investmentsForecast = calculateForecastBalance(onlyAssets, openingBalances.investments, investmentsGrowth, growthsAndRisks.investmentsRisk)
  const fixedAssetsForecast = calculateForecastBalance(onlyAssets, openingBalances.fixedAssets, fixedAssetsGrowth, growthsAndRisks.fixedAssetsRisk)
  const totalForecast = homeLoansForecast + businessLoansForecast + creditCardsForecast + institutionalBankingForecast + offshoreBankForecast + wealthForecast + investmentsForecast + fixedAssetsForecast

  return { totalForecast, homeLoansForecast, businessLoansForecast, creditCardsForecast, institutionalBankingForecast, offshoreBankForecast, wealthForecast, investmentsForecast, fixedAssetsForecast }
}

export const calculateTotalOpeningBalance = (openingBalances) => {
  return [
    openingBalances.homeLoans,
    openingBalances.businessLoans,
    openingBalances.creditCards,
    openingBalances.institutionalBanking,
    openingBalances.offshoreBank,
    openingBalances.wealthAssets,
    openingBalances.investments,
    openingBalances.fixedAssets
  ].reduce((result, balance) => {
    result += balance
    return result
  }, 0)
}

export const calculateGrowthPerBusiness = (totalOpeningValue, totalGrowthPercentage, forecastValues, openingBalances) => {
  return [
    { openingBalanceName: 'homeLoans', forecastValuesName: 'homeLoansForecast', growthName: 'homeLoansGrowth' },
    { openingBalanceName: 'businessLoans', forecastValuesName: 'businessLoansForecast', growthName: 'businessLoansGrowth' },
    { openingBalanceName: 'creditCards', forecastValuesName: 'creditCardsForecast', growthName: 'creditCardsGrowth' },
    { openingBalanceName: 'institutionalBanking', forecastValuesName: 'institutionalBankingForecast', growthName: 'institutionalBankingGrowth' },
    { openingBalanceName: 'offshoreBank', forecastValuesName: 'offshoreBankForecast', growthName: 'offshoreBankGrowth' },
    { openingBalanceName: 'wealthAssets', forecastValuesName: 'wealthForecast', growthName: 'wealthGrowth' },
    { openingBalanceName: 'investments', forecastValuesName: 'investmentsForecast', growthName: 'investmentsGrowth' },
    { openingBalanceName: 'fixedAssets', forecastValuesName: 'fixedAssetsForecast', growthName: 'fixedAssetsGrowth' }
  ].reduce((result, { openingBalanceName, forecastValuesName, growthName }) => {
    const growthApportioned = (totalOpeningValue + totalGrowthPercentage) * (forecastValues[forecastValuesName] / forecastValues.totalForecast) - forecastValues[forecastValuesName]
    let growthPercentage = 0
    try {
      growthPercentage = ((forecastValues[forecastValuesName] + growthApportioned) / openingBalances[openingBalanceName]) - 1
      growthPercentage = parseInt((growthPercentage * 100).toFixed(0) * 100) || 0
    } catch (err) {}
    result[growthName] = growthPercentage
    return result
  }, {})
}

export const calculateCapitalMovement = (totalRWA, totalCapitalDeductions, decisions, openingBalances) => {
  const rwaCapital = totalRWA * (decisions.capitalRatioChange / 10000)
  const totalShareHoldersFundsRequired = rwaCapital + Math.abs(totalCapitalDeductions)
  const capitalMovement = totalShareHoldersFundsRequired - openingBalances.shareholdersFunds
  return capitalMovement
}

const calculateRWA = (forecastBalance, riskWeight) => {
  if (riskWeight <= 0) {
    return 0
  }
  return forecastBalance * (riskWeight / 10000)
}

export const calculateRiskWeightedAssets = (forecasts, decisions) => {
  const homeLoansRiskWeightedAsset = calculateRWA(forecasts.homeLoansForecast, decisions.homeLoansRisk)
  const businessLoansRiskWeightedAsset = calculateRWA(forecasts.businessLoansForecast, decisions.businessLoansRisk)
  const creditCardsRiskWeightedAsset = calculateRWA(forecasts.creditCardsForecast, decisions.creditCardsRisk)
  const institutionalBankingRiskWeightedAsset = calculateRWA(forecasts.institutionalBankingForecast, decisions.institutionalBankingRisk)
  const offshoreBankRiskWeightedAsset = calculateRWA(forecasts.offshoreBankForecast, decisions.offshoreBankRisk)
  const wealthRiskWeightedAsset = calculateRWA(forecasts.wealthForecast, decisions.wealthRisk)
  const investmentsRiskWeightedAsset = calculateRWA(forecasts.investmentsForecast, decisions.investmentsRisk)
  const fixedAssetsRiskWeightedAsset = calculateRWA(forecasts.fixedAssetsForecast, decisions.fixedAssetsRisk)
  const totalCreditRiskWeightedAsset = homeLoansRiskWeightedAsset + businessLoansRiskWeightedAsset + creditCardsRiskWeightedAsset + institutionalBankingRiskWeightedAsset + offshoreBankRiskWeightedAsset + wealthRiskWeightedAsset + investmentsRiskWeightedAsset + fixedAssetsRiskWeightedAsset

  const operationalRiskWeightedAsset = calculateRWA(totalCreditRiskWeightedAsset, decisions.opexRisk)

  const totalRiskWeightedAsset = totalCreditRiskWeightedAsset + operationalRiskWeightedAsset

  return {
    homeLoansRiskWeightedAsset,
    businessLoansRiskWeightedAsset,
    creditCardsRiskWeightedAsset,
    institutionalBankingRiskWeightedAsset,
    offshoreBankRiskWeightedAsset,
    wealthRiskWeightedAsset,
    investmentsRiskWeightedAsset,
    fixedAssetsRiskWeightedAsset,
    totalCreditRiskWeightedAsset,
    operationalRiskWeightedAsset,
    totalRiskWeightedAsset
  }
}

const calculateDeduction = (openingVal, growth, riskWeight) => {
  if (riskWeight !== -1) {
    return 0
  }
  return openingVal * -1
}

export const calculateForecastDeductions = (forecasts, decisions) => {
  const homeLoansForecastDeduction = calculateDeduction(forecasts.homeLoansForecast, decisions.homeLoansGrowth, decisions.homeLoansRisk)
  const businessLoansForecastDeduction = calculateDeduction(forecasts.businessLoansForecast, decisions.businessLoansGrowth, decisions.businessLoansRisk)
  const creditCardsForecastDeduction = calculateDeduction(forecasts.creditCardsForecast, decisions.creditCardsGrowth, decisions.creditCardsRisk)
  const institutionalBankingForecastDeduction = calculateDeduction(forecasts.institutionalBankingForecast, decisions.institutionalBankingGrowth, decisions.institutionalBankingRisk)
  const offshoreBankForecastDeduction = calculateDeduction(forecasts.offshoreBankForecast, decisions.offshoreBankGrowth, decisions.offshoreBankRisk)
  const wealthForecastDeduction = calculateDeduction(forecasts.wealthForecast, decisions.wealthGrowth, decisions.wealthRisk)
  const investmentsForecastDeduction = calculateDeduction(forecasts.investmentsForecast, decisions.investmentsGrowth, decisions.investmentsRisk)
  const fixedAssetsForecastDeduction = calculateDeduction(forecasts.fixedAssetsForecast, decisions.fixedAssetsGrowth, decisions.fixedAssetsRisk)
  const totalCapitalDeductions = homeLoansForecastDeduction + businessLoansForecastDeduction + creditCardsForecastDeduction + institutionalBankingForecastDeduction + offshoreBankForecastDeduction + wealthForecastDeduction + investmentsForecastDeduction + fixedAssetsForecastDeduction

  return {
    homeLoansForecastDeduction,
    businessLoansForecastDeduction,
    creditCardsForecastDeduction,
    institutionalBankingForecastDeduction,
    offshoreBankForecastDeduction,
    wealthForecastDeduction,
    investmentsForecastDeduction,
    fixedAssetsForecastDeduction,
    totalCapitalDeductions
  }
}
